import React, { Component } from "react"
import BaseModal, { ModalActions, DeleteButton, CancelButton } from "../../../../containers/UI/Modal/BaseModal"
import InfoRow from "../../../configuration/Rows/InfoRow/InfoRow"
import ButtonRow from "../../../configuration/shared/ButtonRow/ButtonRow"
import QRCode from "react-qr-code"
import label from "./LabelDesign.css"
import axios from "../../../../axios"
import { toPng } from "html-to-image"

// Images
import Emblem from "./icons/Allorado.svg"
import CE from "./icons/CE.svg"
import KNX from "./icons/KNX.svg"
import WEEE from "./icons/WEEE.svg"


class GatewayModal extends Component {

  state = {
    license: {},
  }

  licenseTypes = {
    "master": "Master gateway",
    "slave": "Forwarder gateway",
    "knx-iot-router": "KNX IoT router",
    "knx-lora-gateway": "KNX LoRa gateway",
    "knx-lora-forwarder": "KNX LoRa forwarder",
    "evolution": "Evolution plug-in"
  }
  productNames = {
    "master": "Protocol Gateway",
    "slave": "Forwarder Gateway",
    "knx-iot-router": "KNX IoT Router",
    "knx-lora-gateway": "LoRa Gateway",
    "knx-lora-forwarder": "LoRa Forwarder",
    "evolution": "Evolution plug-in"
  }

  labelRef = React.createRef()

  componentDidMount() {
    this.setState({ license: this.props.license })
  }

  downloadLicenseHandler = () => {
    
    // Download file
    const fileName = this.state.license.gateway.serialNumber + (((this.state.license.formatVersion ?? 1) >= 1.9) ? '.lic' : '.pem')
    const blob = new Blob([this.state.license.licenseText], { type:"application/text" })
    const href = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = href
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  downloadLabelHandler = () => {

    // Convert to PNG
    toPng(this.labelRef.current, { cacheBust: false, style: { display: "block" }, pixelRatio: 5 }).then((url) => {

      // Direct print from Safari is impossible, as Safari constantly fucks up
      // margins and created multiple pages etc, a rathole not worth it

      // Download the PNG
      let fileName = this.state.license.gateway.serialNumber + "-label.png"
      let link = document.createElement("a")
      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

    }).catch((err) => {
      console.log(err)
    })
  }

  deleteHandler = () => {

    if (window.confirm("CAREFUL! Are you sure you would like to delete this license?") === true) {

      // Request deletion
      axios.get("/license/delete/" + this.props.license._id)

      // Close the modal
      this.props.closeModal()
    }
  }


  render() {

    // Determine license type
    let licenseType = this.state.license.licenseType ?? ""

    // Construct license info
    let licenseTypeInfo = ""
    if (this.state.license) {
      let verification = "UUID-based license  🔒⚠️"
      if (this.state.license.verification) {
        let security = (this.state.license.verification === "TPM-based") ? " license  🔒✅" : " license  🔒⚠️"
        verification = this.state.license.verification + security
      }
      licenseTypeInfo = `Format ${(this.state.license.formatVersion ?? 0).toFixed(1)} - ${verification}`
    }

    // Construct radio info
    let radioInfo = ""
    if ("enableInternalRadio" in this.state.license) {
      radioInfo = `Internal radio ${this.state.license.enableInternalRadio ? 'enabled' : 'disabled'} - `
    } else {
      radioInfo = "No internal restriction - "
    }
    if (this.state.license.forwarderCount) {
      radioInfo += this.state.license.forwarderCount + " forwarder" + ((this.state.license.forwarderCount !== 1) ? "s" : "")
    } else {
      radioInfo += "no forwarder restriction"
    }


    return (
      <BaseModal 
        isOpen={this.props.shouldShow} 
        onClose={this.props.closeModal}
      >
        { licenseType.startsWith("knx-") ? this.knxDeviceLabel() : this.protocolGatewayHeader() }

        <ButtonRow
          title="License file"
          ultrawide firstRow
          body={`Download .${((this.state.license.formatVersion ?? 1) >= 1.9) ? 'lic' : 'pem'} file`}
          onClick={this.downloadLicenseHandler}/>
        <ButtonRow
          title="Hardware label"
          ultrawide
          show={licenseType.startsWith("knx-")}
          body="Download label"
          onClick={this.downloadLabelHandler}/>

        <hr style={{ border: "0.7px solid #FF6315", margin: "10px 0 10px 0" }}/>

        <InfoRow
          title="Gateway"
          firstRow
          body={(this.state.license.gateway) ? this.state.license.gateway.trivialName + " - " + this.state.license.gateway.serialNumber : ""}/>
        <InfoRow
          title="Validity window"
          body={this.state.license ? `Valid from ${this.formatDate(this.state.license.startDate)} to ${this.formatDate(this.state.license.endDate)}` : ""}/>
        <InfoRow
          title="License format"
          body={licenseTypeInfo}/>
        <InfoRow
          show={Boolean(this.state.license.whiteLabelVendor)}
          title="White-label vendor"
          body={this.state.license.whiteLabelVendor}/>

        <hr style={{ border: "0.7px solid #FF6315", margin: "10px 0 10px 0", display: (licenseType.startsWith("knx-")) ? "none" : "block" }}/>

        <InfoRow
          firstRow
          show={licenseType === "master"}
          title="Radio restrictions"
          body={radioInfo}/>
        <InfoRow
          firstRow={licenseType === "evolution"}
          show={licenseType === "master" || licenseType === "evolution"}
          title="LoRa device limit"
          body={(this.state.license.loraDeviceLimit) ? (this.state.license.loraDeviceLimit + " devices") : "Not enforced"}/>
        <InfoRow
          show={licenseType === "master" && this.state.license.formatVersion >= 1.3}
          title="Protocol conversion"
          body={!(this.state.license.protocols === undefined || this.state.license.protocols.length === 0) ? (this.state.license.protocols.join(", ")) : "No protocols allowed"}/>

        <ModalActions>
          <CancelButton onClick={this.props.closeModal}>Close</CancelButton>
          <DeleteButton onClick={this.deleteHandler}/>
        </ModalActions>
      </BaseModal>
    );
  }

  knxDeviceLabel = () => {

    // Prepare label data
    let labelData = {}
    let licenseType = this.state.license.licenseType ?? ""

    if (this.state.license) {
    
      // Prepare label content
      if (licenseType.startsWith("knx-")) {
        
        // Populate product name based on license
        labelData.productName = this.productNames[licenseType]

        if (this.state.license.gateway) {

          // Retrieve gateway object
          let gateway = this.state.license.gateway

          // Format CM4 serial number as MAC address
          let serialPairs = gateway.serialNumber.match(/.{1,2}/g)
          if (serialPairs.length === 6) {
            labelData.mac = serialPairs.join(":")
          } else {
            labelData.mac = "N/A"
          }

          // Compose QR code
          labelData.knxString = `KNX:S:${gateway.serialNumber};P:${gateway.knxFDSK}`
        }
      }
    }

    return (
      <div className={label.labelContainer}>
        <div ref={this.labelRef} className={label.printContainer}>
          <div className={label.topRow}>
            <img className={label.alloradoLogo} src={Emblem} alt="emblem"/>
            <div className={label.titleContainer}>
              <label className={label.productTitle}>{labelData.productName}</label>
              <label className={label.macAddress}><span>MAC:</span> {labelData.mac}</label>
            </div>
          </div>
            
          <div className={label.bottomRow}>
            <div className={label.qrCode}>
              <QRCode level={"M"} value={labelData.knxString} style={{ height: "100%", width: "auto" }}/>
            </div>
            <div className={label.credentialContainer}>
              <img className={label.knxLogo} src={KNX} alt="knx"/>
              <label className={label.credentialText}>{labelData.knxString}</label>
            </div>
            <img className={label.ceLogo} src={CE} alt="ce"/>
            <img className={label.weeeLogo} src={WEEE} alt="weee"/>
          </div>
        </div>

        <div className={label.borderContainer}></div>
      </div>
    )
  }

  protocolGatewayHeader = () => {

    // Prepare data
    let licenseType = this.state.license.licenseType ?? ""

    // Determine product name
    let productName = this.productNames[licenseType]

    // Determine serial
    let serial = ""
    if (this.state.license) {
      if (this.state.license.gateway) {
        serial = this.state.license.gateway.serialNumber
      }
    }

    return (
      <div className={label.labelContainer} style={{ height: "140px", marginTop: "0" }}>
        <div ref={this.labelRef} className={label.printContainer} style={{ height: "140px"}}>
          <div className={label.topRow}>
            <img className={label.alloradoLogo} src={Emblem} alt="emblem" style={{ width: "100px"}}/>
            <div className={label.titleContainer}>
              <label className={label.productTitle}>{productName}</label>
              <label className={label.macAddress}><span>S/N:</span> {serial}</label>
            </div>
          </div>
        </div>

        <div className={label.borderContainer} style={{ height: "120px", marginTop: "-140px" }}></div>
      </div>
    )
  }

  formatDate = (epoch) => {
    // Create date
    var date = new Date(0)
    date.setUTCSeconds(epoch)
    // Format properly
    return date.getDate() + "-" + (date.getMonth() + 1) + "-"  + date.getFullYear()
  }
}

export default GatewayModal;
